import { useEffect, useState } from "react";
import { HeartPulse, ShieldAlert, PhoneCall } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import LoadFailureNote from "./LoadFailureNote";
import api from "../lib/api";
import {
  COACH_ABSENCE_WARNING,
  DRAWN_COACH_ABSENCE_WARNING,
  NOT_PROVIDED,
  displayValue,
  formatAuditLine,
  formatEmergencyContact,
} from "../lib/playerHealth";

/**
 * The coach's read of a linked player's health record (issue #287), on the
 * coach portal, directly under the player they have selected.
 *
 * ============================================================
 * WHY IT IS A CARD AND NOT A TAB
 * ============================================================
 * Everything else about a player on this page is behind a tab — goals,
 * feedback, reflections. This is not, deliberately. The use case #287 was
 * filed for is a coach knowing about the asthmatic, the epileptic and the
 * kid with the nut allergy *before* something happens rather than after,
 * and a fact you have to remember to go and look for is a fact you find out
 * about afterwards. The emergency contact makes that sharper still: the
 * moment it is needed is the moment nobody is navigating a tab strip.
 *
 * The counter-argument — a child's medical history and a parent's phone
 * number sitting open on a phone screen at a public ground — is real and
 * was weighed. It loses to the first argument: hiding it behind a tap
 * reintroduces exactly the failure this feature exists to prevent, and the
 * coach can already read it, so a collapse control protects nobody from the
 * coach and only from whoever is standing behind them. If that trade is
 * ever revisited, revisit it here, with that sentence in front of you.
 *
 * ORDER: emergency contact first, then conditions, then accessibility needs
 * — Martin's decision, 2026-08-18, and the same order the player's entry
 * card uses. A coach who has learned where to look should find it where the
 * player left it.
 *
 * ============================================================
 * ABSENCE IS RENDERED, LOUDLY, AS AN ABSENCE
 * ============================================================
 * A blank field renders the words `NOT_PROVIDED` in grey italics, never an
 * empty line and never a dash — and `COACH_ABSENCE_WARNING` sits under the
 * whole panel saying in plain English that it does not mean there is
 * nothing to know. This is the single most important behaviour in the
 * component: a coach who reads white space as "no conditions", or an empty
 * contact row as "there is nobody to ring", is the harm the ticket is
 * written against, and white space is what a naive `{value}` render
 * produces.
 *
 * A record NOBODY has ever filled in is stated as its own thing ("Nothing
 * has been entered for this player") rather than three repetitions of "Not
 * provided" — identical greys read as a rendering bug, and a coach skims
 * past a rendering bug.
 *
 * ============================================================
 * WHAT IT DOES ON FAILURE
 * ============================================================
 * `LoadFailureNote`, never an empty panel (#271). On this surface the
 * swallowed-failure lie is at its worst: an empty panel says "this child
 * has no medical conditions and no emergency contact", which are claims
 * about a person, made by a network error.
 *
 * ADMIN SEES AN EXPLANATION, NOT AN EMPTY PANEL. The API denies admin
 * outright (404 — Martin, 2026-08-18: the record "must not be reachable
 * from an admin-scoped query at all"), so this component does not fetch for
 * a non-coach at all; it says why. Hiding the panel from admin would make a
 * deliberate product decision look like a surface that just is not there —
 * the same choice #205 made for the Reflections tab.
 *
 * `variant="coach-player"` (issue #973, D72/D186.1) is an opt-in
 * presentation, default unchanged: `CoachPlayer.dc.html` loses the gold
 * border and tint for a navy left rule ("gold is the action only", the
 * artboard's own comment) — this is the ONE place D72 stays alive per D86.
 * Every state, id and the SAME SINGLE `GET /players/${playerId}/health`
 * call are untouched; only the outer card's own colour changes.
 */
export default function PlayerHealthPanel({ playerId, playerName, viewerRole, variant = "default" }) {
  const [record, setRecord] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isCoach = viewerRole === "coach";

  const load = () => {
    if (!playerId || !isCoach) {
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    setFailed(false);
    return api
      .get(`/players/${playerId}/health`)
      .then((r) => {
        setRecord(r.data);
        setLoading(false);
      })
      .catch(() => {
        setRecord(null);
        setFailed(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, viewerRole]);

  const auditLine = formatAuditLine(record);
  const contact = formatEmergencyContact(record);
  const anyContent =
    !!record &&
    (contact !== null ||
      displayValue(record.medical_conditions) !== null ||
      displayValue(record.accessibility_needs) !== null);

  const isDrawn = variant === "coach-player";

  // #1221 slice 1 (D254 Q15): the coach-player variant sits in the kit's
  // frame (the same surface #1236 used on the guide page), not a shadcn
  // `Card`. The navy left rule (D72) is the drawn health card's own and stays.
  // Every other variant keeps `Card` exactly as before.
  const Frame = isDrawn ? KitFrame : ShippedFrame;

  return (
    <Frame>
        <div className="flex items-center gap-2 min-w-0">
          {/* D72/D186 — the coach-player variant's icon wrapper is the
              drawn light blue (`CoachPlayer.dc.html`'s `#E8F1FB`), not
              gold: the health card carries no gold at all on that
              artboard (journey 29's own zero-gold row for this screen). */}
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: isDrawn ? "#E8F1FB" : "#D4A01722" }}
          >
            <HeartPulse size={16} color={isDrawn ? "#0B2E59" : "#8a6a0f"} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-base text-slate-900 break-words">
              Health and emergency contact
            </h3>
            <p className="text-xs text-slate-600 break-words">
              Entered by {playerName || "the player"}'s account, and shared with you because you're linked.
            </p>
          </div>
        </div>

        {!isCoach && (
          <p className="text-sm text-slate-600 break-words" data-testid="cp-health-not-for-admin">
            Health details and emergency contacts are shown to the player's linked coaches only. Platform
            admins don't see them.
          </p>
        )}

        {isCoach && failed && (
          <LoadFailureNote
            what="this player's health details"
            onRetry={load}
            testId="cp-health-load-failed"
          />
        )}

        {isCoach && !failed && loading && (
          <p className="text-sm text-slate-500" data-testid="cp-health-loading">
            Loading…
          </p>
        )}

        {isCoach && !failed && !loading && (
          <>
            {!anyContent && (
              <p className="text-sm text-slate-700 break-words" data-testid="cp-health-nothing-entered">
                Nothing has been entered for this player.
              </p>
            )}

            <dl className="space-y-3">
              {/* First, and visually weightiest — it is the one somebody
                  reads while something is going wrong. */}
              <HealthField
                label="Emergency contact"
                value={contact}
                icon={<PhoneCall size={13} className="inline mr-1.5 -mt-0.5" color="#8a6a0f" />}
                testId="cp-health-contact"
              />
              <HealthField
                label="Medical conditions and allergies"
                value={record?.medical_conditions}
                testId="cp-health-conditions"
              />
              <HealthField
                label="Disability or accessibility needs"
                value={record?.accessibility_needs}
                testId="cp-health-needs"
              />
            </dl>

            <div className="flex items-start gap-2 pt-1 min-w-0">
              <ShieldAlert size={14} color="#8a6a0f" className="mt-0.5 shrink-0" />
              {/* PR #980 round 2, U2 — on the drawn (coach-player) variant,
                  the token check found this computed to #334155 (the
                  platform-wide WCAG bump `index.css` applies to
                  `text-slate-600`), not the drawn `--nw-ink-lede` (#475569).
                  Scoped to `isDrawn`: the default variant keeps its
                  existing Tailwind class untouched. */}
              <p
                className={isDrawn ? "text-xs break-words" : "text-xs text-slate-600 break-words"}
                style={isDrawn ? { color: "var(--nw-ink-lede)" } : undefined}
                data-testid="cp-health-absence-warning"
              >
                {/* PR #980 round 3, N2 — the drawn quote marks are curly,
                    but `COACH_ABSENCE_WARNING` is the SHIPPED constant
                    (straight quotes), read here by this file's own DEFAULT
                    variant too — round 2's U1 fix changed it in place and
                    leaked the curly quotes into the flag-off panel. The
                    curly-quote wording is now a separate export, used only
                    on this drawn variant. */}
                {isDrawn ? DRAWN_COACH_ABSENCE_WARNING : COACH_ABSENCE_WARNING}
              </p>
            </div>

            {auditLine ? (
              <p className="text-xs text-slate-500 break-words" data-testid="cp-health-audit-line">
                {auditLine}. That is the account that saved it — for a junior, often a parent or guardian.
              </p>
            ) : (
              <p className="text-xs text-slate-500 break-words" data-testid="cp-health-never-saved">
                No one has ever saved this record.
              </p>
            )}
          </>
        )}
    </Frame>
  );
}

const KIT_FRAME_STYLE = {
  padding: 16,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderLeft: "3px solid #0B2E59",
  borderRadius: "var(--nw-radius-card)",
};

function KitFrame({ children }) {
  return (
    <div className="space-y-3 min-w-0" style={KIT_FRAME_STYLE} data-testid="cp-health-panel">
      {children}
    </div>
  );
}

function ShippedFrame({ children }) {
  return (
    <Card className="border-2 border-[#D4A017]/50 bg-[#D4A017]/[0.04]" data-testid="cp-health-panel">
      <CardContent className="p-4 sm:p-5 space-y-3 min-w-0">{children}</CardContent>
    </Card>
  );
}

/**
 * One field. An absent value gets the WORDS, in a visually distinct
 * treatment — grey and italic so it cannot be mistaken for something the
 * player wrote, but present, so it cannot be mistaken for nothing to
 * report.
 */
function HealthField({ label, value, icon, testId }) {
  const shown = displayValue(value);
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd
        className={`text-sm mt-0.5 break-words whitespace-pre-wrap ${
          shown ? "text-slate-900 font-medium" : "text-slate-400 italic"
        }`}
        data-testid={testId}
      >
        {shown ? (
          <>
            {icon}
            {shown}
          </>
        ) : (
          NOT_PROVIDED
        )}
      </dd>
    </div>
  );
}
